import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { ProductComponentDef } from '@df/catalog-ui/product/product.component.def';
import { Category } from '@df/catalog/category/category';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { TimeoutService } from '@df/ng/timeout.service';
import { WishlistItem } from '@df/session/api/wishlist/wishlist-item';
import { findMediaByLabel } from '@df/ui/common/find-media-by-label.pipe';
import { TradeService } from '../../../checkout/module/trade/trade.service';
import { TPromoBar } from '../product-promo-bar/product-promo-bar.component';
import { Product } from '../product.decorator';

@Component({
    selector: 'product',
    templateUrl: 'product.html',
    styleUrls: ['product.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductComponent extends ProductComponentDef<Product<IProductDataV2>> implements AfterContentInit {
    protected qty = 1;
    /**
     * Optional wishlist item in wishlist context
     */
    @Input()
    wishlistItem?: WishlistItem;

    @Input()
    hideButtons = false;

    @Input()
    searchTerm?: string;

    @Input()
    klevuRecommendationId?: string;

    /**
     * If pro price mode is active in promobar
     */
    protected isProPriceMode = false;

    /**
     * Image height for 2+ cols layout
     */
    private imageHeightStyle?: string;

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        app: App,
        timeoutService: TimeoutService,
        elementRef: ElementRef,
        private tradeService: TradeService,
        private renderer2: Renderer2
    ) {
        super(changeDetectorRef, app, timeoutService, elementRef);
        this.subscriptions.add(this.app.customer.model.obs.data.subscribe(() => this.detectChanges()));
    }

    onQtyChange(qty: number | string): void {
        this.qty = qty as number;
        this.detectChanges();
    }

    /**
     * On init actions
     */
    ngAfterContentInit() {
        this.timeoutService.setTimeout(() => {
            this.renderer2.setStyle(this.elementRef.nativeElement as HTMLElement, 'visibility', 'visible');
        });
    }

    /**
     * Retrieves image padding = x = height/width * 100
     *
     * We used this rather then ratio to make thing match more accurately without creating new ratios
     */
    getImageHeightStyle(): string {
        if (!this.imageHeightStyle || !this.requiresSizeWatch()) {
            return `${this.get1x1Ratio() * 100}%`;
        }
        return this.imageHeightStyle;
    }

    /**
     * Gets product image for given layout
     */
    getImage(): string | undefined {
        if (this.model.data?.isBanner && this.model.data.media && this.client.sizeId < 2) {
            return findMediaByLabel(this.model.data.media, 'mobile') || this.model.data?.image;
        }
        if (this.getLayout() === '2x1') {
            return this.model.data?.img_horizontal || this.model.data?.image;
        }
        return this.model.data?.image;
    }

    /**
     * Gets product image for given layout
     */
    getAlternativeImage(): string | undefined {
        if (this.getLayout() === '2x1') {
            return undefined;
        }
        return this.model.data?.img_alternative;
    }

    /**
     * If size watch is required
     */
    protected override requiresSizeWatch() {
        return this.getLayout() !== '1x1';
    }

    /**
     * On component size change callback
     */
    protected override onSizeChange(dims: { height: number; width: number }) {
        this.imageHeightStyle = this.listing?.plugin.grid.getImageHeight(this.get1x1Ratio(), this.gridColumn || 1, dims.width);
        this.detectChanges();
    }

    /**
     * Returns image ration for 1x1 column dependin on product type
     */
    private get1x1Ratio(): number {
        return 1;
    }

    /**
     * on mobile we always use 1x1 layout as we have up to 2 cols in grid
     * and 2-col item take a whole row then and keeps the original ratio
     */
    override getLayout() {
        if (this.client.isS) {
            return '1x1';
        }
        return super.getLayout();
    }

    get isTradeScope(): boolean {
        return this.tradeService.isTradeCustomer();
    }

    protected get klevuSearchTrackClick(): KlevuSearch.ITrackSearchProductClickData | undefined {
        if (this.searchTerm) {
            return {
                product: this.model,
                term: this.searchTerm
            };
        }
    }

    protected get klevuCategoryProductTrackClick(): KlevuSearch.ITrackCategoryProductClickData | undefined {
        if (this.listing?.config.source?.type === Category.type) {
            return {
                product: this.model,
                category: this.listing.config.source,
                position: typeof this.listPosition === 'number' ? this.listPosition - 1 : undefined,
                searchType: this.listing?.sort?.getSelection()?.engine === 'klevu' ? KlevuSearch.catNavSearchType : undefined
            };
        }
        return undefined;
    }
    protected get klevuRecommendationsProductClickData():
        | KlevuSearch.IAnalyticsRecommendationTrackData<KlevuSearch.EAnalyticsRecommendationEvent.select>
        | undefined {
        if (this.klevuRecommendationId && this.model && typeof this.listPosition === 'number') {
            return <KlevuSearch.IAnalyticsRecommendationTrackData<KlevuSearch.EAnalyticsRecommendationEvent.select>>{
                event: KlevuSearch.EAnalyticsRecommendationEvent.select,
                eventListId: this.klevuRecommendationId,
                products: [{ model: this.model, position: this.listPosition }]
            };
        }
        return undefined;
    }

    protected handlePromoBarChange(promoBar: TPromoBar) {
        const isProPriceMode = promoBar?.type === 'becomePro' && this.tradeService.isTradeCustomer();
        if (isProPriceMode !== this.isProPriceMode) {
            this.isProPriceMode = isProPriceMode;
            this.detectChanges();
        }
    }
}
